import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarClock,
  CheckCircle2,
  Hourglass,
  Inbox,
  ShieldAlert,
  Siren,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { SkeletonLines } from '@/components/ui/Skeleton';
import { formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { PlatformStats, SubscriptionIncome } from '@/types/institute';
import type { TicketQueueCounts } from '@/types/support';
import { LIVE_TICKETS } from '../support/api/support.api';

interface AttentionListProps {
  stats: PlatformStats | null;
  /** The money row's figures, already fetched by the dashboard. */
  income: SubscriptionIncome | null;
  /** The support desk's counts, already fetched by the dashboard. */
  ticketCounts: TicketQueueCounts | null;
  isLoading: boolean;
}

function institutesCount(count: number): string {
  return `${formatNumber(count)} ${count === 1 ? 'institute' : 'institutes'}`;
}

type Urgency = 'critical' | 'warning' | 'info';

const URGENCY = {
  critical: {
    chip: 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400',
    border: 'border-rose-200 dark:border-rose-900/60',
  },
  warning: {
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-900/60',
  },
  info: {
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-900/60',
  },
} as const satisfies Record<Urgency, Record<string, string>>;

interface AttentionRow {
  key: string;
  urgency: Urgency;
  icon: LucideIcon;
  title: string;
  detail: string;
  to: string;
  action: string;
}

export function AttentionList({ stats, income, ticketCounts, isLoading }: AttentionListProps) {
  const rows: AttentionRow[] = [];

  if (stats) {
    if (stats.totalInstitutes === 0) {
      rows.push({
        key: 'empty',
        urgency: 'info',
        icon: Building2,
        title: 'No customers yet',
        detail: 'Add the first institute to hand the product over.',
        to: '/platform/institutes/new',
        action: 'Add institute',
      });
    }

    if (stats.suspendedInstitutes > 0) {
      rows.push({
        key: 'suspended',
        urgency: 'critical',
        icon: ShieldAlert,
        title: `${formatNumber(stats.suspendedInstitutes)} ${
          stats.suspendedInstitutes === 1 ? 'institute is' : 'institutes are'
        } suspended`,
        detail: 'Nobody there can sign in. Data is untouched.',
        to: '/platform/institutes?status=SUSPENDED',
        action: 'Review',
      });
    }

    if (stats.pendingSetup > 0) {
      rows.push({
        key: 'pending',
        urgency: 'warning',
        icon: Hourglass,
        title: `${formatNumber(stats.pendingSetup)} ${
          stats.pendingSetup === 1 ? 'institute has' : 'institutes have'
        } not finished setting up`,
        detail: 'Invited, but their administrator has not taken the account over yet.',
        to: '/platform/institutes?status=PENDING_SETUP',
        action: 'Chase',
      });
    }
  }

  // The institutes list has no payment filter, so these open the list itself
  // and say so, rather than promise the overdue ones.
  if (income) {
    const overdue = income.dueFromLastMonth;
    if (overdue.instituteCount > 0) {
      rows.push({
        key: 'overdue',
        urgency: 'critical',
        icon: AlertTriangle,
        title: `${institutesCount(overdue.instituteCount)} overdue on their fee`,
        detail: `${formatCurrency(overdue.amount)} unpaid past its due date.`,
        to: '/platform/institutes',
        action: 'View institutes',
      });
    }

    const dueSoon = income.dueThisMonth;
    if (dueSoon.instituteCount > 0) {
      rows.push({
        key: 'due-this-month',
        urgency: 'warning',
        icon: CalendarClock,
        title: `${institutesCount(dueSoon.instituteCount)} due to pay by the month's end`,
        detail: `${formatCurrency(dueSoon.amount)} unpaid, falling due this month.`,
        to: '/platform/institutes',
        action: 'View institutes',
      });
    }
  }

  if (ticketCounts) {
    if (ticketCounts.urgent > 0) {
      rows.push({
        key: 'urgent-tickets',
        urgency: 'critical',
        icon: Siren,
        title: `${formatNumber(ticketCounts.urgent)} urgent ${
          ticketCounts.urgent === 1 ? 'ticket is' : 'tickets are'
        } not yet resolved`,
        detail: 'Marked urgent by the support desk.',
        to: `/platform/support?status=${LIVE_TICKETS}&priority=URGENT`,
        action: 'Answer',
      });
    }

    // Urgent ones already have their own row, so this counts the rest.
    const otherOpen = ticketCounts.open - ticketCounts.urgent;
    if (otherOpen > 0) {
      rows.push({
        key: 'open-tickets',
        urgency: 'warning',
        icon: Inbox,
        title: `${formatNumber(otherOpen)} ${ticketCounts.urgent > 0 ? 'other ' : ''}${
          otherOpen === 1 ? 'ticket is' : 'tickets are'
        } not yet resolved`,
        detail: 'Open, in progress or waiting on the school.',
        to: `/platform/support?status=${LIVE_TICKETS}`,
        action: 'Open queue',
      });
    }
  }

  // "All clean" is a claim about every source; if one of them did not load,
  // say only what the loaded ones show.
  const everythingLoaded = stats !== null && income !== null && ticketCounts !== null;

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-bold text-slate-900 dark:text-white">Wants your attention</h2>
          <p className="mt-0.5 text-[12px] text-slate-500 dark:text-slate-400 font-medium">
            Worked out from the figures above. Each row opens where the job is done.
          </p>
        </div>
        {!isLoading && rows.length > 0 && (
          <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11.5px] font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
            {rows.length}
          </span>
        )}
      </div>

      {isLoading ? (
        <SkeletonLines count={3} />
      ) : rows.length === 0 ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 p-3 text-[12.5px] text-slate-500 font-medium">
          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
          <span>
            {everythingLoaded
              ? 'Nothing waiting on you — all clean.'
              : 'Nothing waiting among the figures that loaded.'}
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row) => {
            const Icon = row.icon;
            const style = URGENCY[row.urgency];

            return (
              <Link
                key={row.key}
                to={row.to}
                className={cn(
                  'group flex items-center justify-between gap-3 rounded-xl border bg-slate-50/60 dark:bg-slate-800/40 p-2.5 transition-all hover:bg-white dark:hover:bg-slate-800 hover:shadow-xs',
                  style.border,
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span
                    className={cn(
                      'grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-transform group-hover:scale-105 shadow-2xs',
                      style.chip,
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-bold text-slate-800 dark:text-slate-200">
                      {row.title}
                    </p>
                    <p className="truncate text-[11px] text-slate-400 dark:text-slate-500 font-medium">
                      {row.detail}
                    </p>
                  </div>
                </div>

                <span className="flex shrink-0 items-center gap-1 text-[12px] font-bold text-[#5b52f9] group-hover:underline">
                  <span>{row.action}</span>
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
